import Papa from 'papaparse'
import type { VariantType } from 'notistack'
import { formatDateByUserTimeZone } from '../helpers/formatDateByUserTimeZone'
import type {
  IBulkSectionStatusResult,
  ISyncTaskSummary,
  ITaskErrorPayload,
  ITaskMetadata,
  ITaskSectionChange,
  ITaskSectionChangeDetail,
  SlotTaskKind,
  TaskPollData,
  TaskPollStatus
} from '../models/Task'

/** Max characters of joined `errorDetails` shown in a sync toast before truncation. */
export const SYNC_TOAST_DETAILS_CHAR_LIMIT = 200

/** Sections named individually in a bulk status toast before the rest become a count. */
export const MAX_NAMED_SECTIONS = 5

export const ACTIVITY_MONITOR_HINT = 'Check the Activity Monitor for more details.'

export interface SlotTaskToastInput {
  status: Exclude<TaskPollStatus, 'PENDING'>
  data?: TaskPollData
  /** Counts + joined error_details from TaskResult.task_metadata when present. */
  taskMetadata?: ITaskMetadata
  /** Stored successMessage or running message — used for SUCCESSFUL subtitle. */
  fallbackMessage: string
  /** Set by the bulk section status actions, whose copy names the operation that ran. */
  kind?: SlotTaskKind
}

export interface SlotTaskToastContent {
  variant: Extract<VariantType, 'success' | 'warning' | 'error'>
  title: string
  /** Plain string so warning/error/success toasts share the same Notification Typography layout. */
  subTitle: string
}

const isSyncTaskSummary = (data: unknown): data is ISyncTaskSummary => {
  if (data == null || typeof data !== 'object' || Array.isArray(data)) return false
  const record = data as Record<string, unknown>
  return (
    typeof record.errors === 'number' ||
    Array.isArray(record.errorDetails) ||
    typeof record.inserted === 'number' ||
    typeof record.updated === 'number'
  )
}

/** Activity Monitor download body (CE-218 ABC-4) — not toast copy. */
export const isSyncErrorCsv = (data: unknown): data is string => {
  if (typeof data !== 'string') return false
  const trimmed = data.trimStart()
  return (
    trimmed.startsWith('Section ID,') ||
    trimmed.startsWith('"Section ID"') ||
    trimmed.startsWith('Section ID\t')
  )
}

export const parseSyncErrorCsvToDetails = (csv: string): string[] => {
  const parsed = Papa.parse<Record<string, string>>(csv, {
    header: true,
    skipEmptyLines: true
  })
  return parsed.data
    .map((row) => {
      const sectionId = (row['Section ID'] ?? row.sectionId ?? '').trim()
      const details = (row.details ?? row.Details ?? '').trim()
      if (sectionId === '' && details === '') return ''
      return sectionId !== '' ? `${sectionId}: ${details}` : details
    })
    .filter(Boolean)
}

const splitJoinedErrorDetails = (joined: string): string[] => {
  return joined
    .split('; ')
    .map((part) => part.trim())
    .filter(Boolean)
}

const getTaskErrorMessage = (data: unknown): string | undefined => {
  // Never surface the downloadable CSV as a toast body.
  if (isSyncErrorCsv(data)) return undefined
  if (typeof data === 'string' && data.trim() !== '') return data
  if (data == null || typeof data !== 'object' || Array.isArray(data)) return undefined
  const record = data as ITaskErrorPayload
  if (typeof record.error === 'string' && record.error.trim() !== '') return record.error
  return undefined
}

/**
 * Same sum the backend uses to pick WARNING (some synced) over ERROR (none). `updated` counts only
 * sections whose dates changed, so up-to-date sections arrive as `unchanged` and count as synced.
 */
export const getSyncedSectionCount = (summary: {
  inserted?: number
  updated?: number
  unchanged?: number
}): number => {
  return (summary.inserted ?? 0) + (summary.updated ?? 0) + (summary.unchanged ?? 0)
}

/**
 * Join error detail lines and truncate to a character budget.
 * Prefer breaking on a space near the limit so we do not mid-cut a word.
 */
export const truncateDetailsByCharLimit = (
  details: string[] | undefined,
  charLimit: number = SYNC_TOAST_DETAILS_CHAR_LIMIT
): { text: string, truncated: boolean } => {
  const joined = (details ?? []).filter(Boolean).join(' ')
  if (joined.length === 0) return { text: '', truncated: false }
  if (joined.length <= charLimit) return { text: joined, truncated: false }

  let cut = joined.slice(0, charLimit)
  const lastSpace = cut.lastIndexOf(' ')
  if (lastSpace > Math.floor(charLimit * 0.5)) {
    cut = cut.slice(0, lastSpace)
  }
  return { text: `${cut.trimEnd()}…`, truncated: true }
}

interface ResolvedSyncToastSource {
  inserted?: number
  updated?: number
  unchanged?: number
  errors?: number
  detailLines: string[]
  hasSyncSignal: boolean
}

/**
 * Resolve toast counts/details from task_metadata (preferred), legacy JSON summary,
 * or ABC-4 CSV in `data` (parsed to Section: reason lines — never raw CSV).
 */
export const resolveSyncToastSource = (
  data: TaskPollData | undefined,
  taskMetadata?: ITaskMetadata
): ResolvedSyncToastSource => {
  const detailLines: string[] = []

  if (typeof taskMetadata?.errorDetails === 'string' && taskMetadata.errorDetails.trim() !== '') {
    detailLines.push(...splitJoinedErrorDetails(taskMetadata.errorDetails))
  } else if (isSyncTaskSummary(data) && Array.isArray(data.errorDetails)) {
    detailLines.push(...data.errorDetails.filter(Boolean))
  } else if (isSyncErrorCsv(data)) {
    detailLines.push(...parseSyncErrorCsvToDetails(data))
  }

  const inserted = taskMetadata?.inserted ?? (isSyncTaskSummary(data) ? data.inserted : undefined)
  const updated = taskMetadata?.updated ?? (isSyncTaskSummary(data) ? data.updated : undefined)
  const unchanged = taskMetadata?.unchanged ?? (isSyncTaskSummary(data) ? data.unchanged : undefined)
  const errorsFromMetaOrSummary =
    taskMetadata?.errors ?? (isSyncTaskSummary(data) ? data.errors : undefined)
  const errors = errorsFromMetaOrSummary ?? (detailLines.length > 0 ? detailLines.length : undefined)

  const hasSyncSignal =
    taskMetadata != null ||
    isSyncTaskSummary(data) ||
    isSyncErrorCsv(data) ||
    detailLines.length > 0

  return { inserted, updated, unchanged, errors, detailLines, hasSyncSignal }
}

const buildWarningLead = (source: ResolvedSyncToastSource): string => {
  const syncedKnown = source.inserted != null || source.updated != null || source.unchanged != null
  const synced = getSyncedSectionCount(source)
  const errors = source.errors ?? source.detailLines.length

  if (syncedKnown) {
    return `${synced} course sections synced, ${errors} could not be synced.`
  }
  if (errors > 0) {
    return `${errors} course sections could not be synced.`
  }
  return 'Some course sections could not be synced.'
}

const buildSyncDetailsSubTitle = (
  lead: string,
  details: string[],
  options: { includeActivityMonitorWhenTruncated: boolean }
): string => {
  const { text, truncated } = truncateDetailsByCharLimit(details)
  const parts = [lead]
  if (text !== '') parts.push(text)
  let body = parts.join(' ')
  if (truncated && options.includeActivityMonitorWhenTruncated) {
    body = `${body}\n${ACTIVITY_MONITOR_HINT}`
  }
  return body
}

/** Per-operation wording for a bulk status change. */
const BULK_STATUS_COPY: Record<SlotTaskKind, {
  changed: string
  none: string
  warningTitle: string
  errorTitle: string
  /** What "wrong status" means for this operation, when the backend sends no detail. */
  wrongStatus: string
  /**
   * What a `partial` section means for this operation. Both mean "the change landed, then something
   * after it did not" — but the something differs, and naming the wrong one sends the admin looking
   * in the wrong place.
   */
  partialLead: string
}> = {
  bulkReopen: {
    changed: 'Reopened',
    none: 'No course section surveys were reopened.',
    warningTitle: 'Bulk reopen completed with warnings',
    errorTitle: 'Bulk reopen failed',
    wrongStatus: 'it is no longer closed',
    // Reopen's `partial` is the project-level work after the commit — the auto-close reschedule and
    // the report-cache refresh. The surveys are open; nothing about student responses is involved.
    partialLead: 'Reopened, but the follow-up refresh did not finish — these surveys are open, so check them rather than running them again:'
  },
  bulkReopenScheduled: {
    changed: 'Scheduled',
    none: 'No course section surveys were scheduled.',
    warningTitle: 'Bulk schedule completed with warnings',
    errorTitle: 'Bulk schedule failed',
    wrongStatus: 'it is no longer closed',
    // Same follow-up work as an immediate reopen -- the auto-close reschedule and the report-cache
    // refresh -- so the same sentence is correct. What differs is only that these surveys are not
    // collecting yet, which the `changed` verb already says.
    partialLead: 'Scheduled, but the follow-up refresh did not finish — these surveys are set up, so check them rather than running them again:'
  },
  bulkGoLive: {
    changed: 'Took',
    none: 'No course section surveys were taken live.',
    warningTitle: 'Bulk go live completed with warnings',
    errorTitle: 'Bulk go live failed',
    wrongStatus: 'it is no longer upcoming',
    // Go-live's `partial` is phase two: the LIVE promotion committed, generating the student
    // responses behind it did not.
    partialLead: 'Live, but their student responses did not finish generating — open these to check them, and do not run them again:'
  }
}

const REASON_COPY: Record<string, string> = {
  // Neutral about *which* date. A scheduled reopen is refused on its start as often as its end,
  // and naming the end there points the admin at the wrong field — in the one sentence whose job
  // is telling them what to change. Found by review.
  boundary: 'those dates are outside the range this survey allows',
  opted_out: 'it is opted out'
  // No `partial` entry: the group it belongs to is already introduced by `partialLead`, which says
  // per operation what half-happened. A second, generic sentence per section repeated it — and for
  // reopen it repeated it wrongly, as a claim about student responses.
}

const isBulkSectionStatusResult = (data: unknown): data is IBulkSectionStatusResult =>
  data != null && typeof data === 'object' && !Array.isArray(data) && Array.isArray((data as IBulkSectionStatusResult).changes)

/** The change landed and something after it did not: done, not failed, and never retried. */
const isPartial = (change: ITaskSectionChange): boolean =>
  change.status === 'success' && change.reason === 'partial'

/**
 * The boundary dates worth naming, in the order the single-section modal already names them.
 *
 * Keyed to the date fields specifically, not `keyof ITaskSectionChangeDetail`: the wider type let
 * `endTimestamp` (a `string | string[]`) into this list, which the date lookup then had to assert
 * away. Narrowed, the compiler rejects a non-date key here instead.
 */
/**
 * The keys that carry a *date*, as opposed to the sentence.
 *
 * Stated as an exclusion of the message-bearing fields so that adding a boundary to the model is
 * enough to make it renderable, while adding a message field is a compile error until it is
 * excluded here — which is what caught the early-access sentence being read as a date.
 */
type MessageFieldKey = 'endTimestamp' | 'startTimestamp' | 'earlyAccessReleaseTimestamp_1' | 'earlyAccessReleaseTimestamp_2' | 'message'
type BoundaryDateKey = Exclude<keyof ITaskSectionChangeDetail, MessageFieldKey>

/**
 * Every boundary the reopen rules can report, and what to call it.
 *
 * `currentEndTimestamp` is on its way out — it comes only from the "24 hours after the previous
 * end" rule, which `feat/reopen-window-rules` deletes. Kept until that merges, because the rule
 * still fires today and dropping the label early would take the date out of a refusal an admin can
 * still hit. After the merge it renders nothing and can go.
 */
const BOUNDARY_DATE_LABELS: Array<[BoundaryDateKey, string]> = [
  ['currentEndTimestamp', 'previously'],
  ['earliestAllowedEndTimestamp', 'earliest allowed'],
  ['latestAllowedEndTimestamp', 'latest allowed'],
  // A scheduled reopen refuses on the *start* as well, with its own pair of keys (Backend #2151).
  // Without these the message rendered and the date silently did not, which is the whole reason a
  // refusal names a date: the admin needs to know what would have been accepted.
  ['earliestAllowedStartTimestamp', 'earliest allowed start'],
  ['latestAllowedStartTimestamp', 'latest allowed start'],
  ['earliestAllowedEarlyAccessReleaseTimestamp_1', 'earliest allowed admin release'],
  ['latestAllowedEarlyAccessReleaseTimestamp_1', 'latest allowed admin release'],
  ['earliestAllowedEarlyAccessReleaseTimestamp_2', 'earliest allowed instructor release'],
  ['latestAllowedEarlyAccessReleaseTimestamp_2', 'latest allowed instructor release']
]

/**
 * The backend's sentence for one section, whatever shape it arrived in.
 *
 * `detail` is a plain sentence for most refusals but a **dict** for `boundary`: the backend sends
 * marshmallow's `ValidationError.messages` verbatim so the dates stay machine-readable, exactly as
 * the per-section 422 body does. Calling `.trim()` on it threw `r.trim is not a function`, and
 * because the terminal handler ran before the task was cleared, the crash replayed from
 * localStorage on every reload — one refused section made the page permanently unusable.
 *
 * A boundary dict is rendered as its message plus the dates that make it actionable, formatted in
 * the viewer's timezone the way `ReopenSurveyModal` formats the same payload. Anything else
 * unreadable returns undefined, so the caller falls back to the reason vocabulary rather than
 * printing `[object Object]`.
 */
export const readChangeDetail = (detail: unknown): string | undefined => {
  if (typeof detail === 'string') {
    const trimmed = detail.trim()
    return trimmed === '' ? undefined : trimmed
  }
  if (detail == null || typeof detail !== 'object' || Array.isArray(detail)) return undefined

  const record = detail as ITaskSectionChangeDetail
  // Marshmallow keys a field error by the field it failed on, so the sentence is under whichever
  // date was rejected — an early-access refusal says nothing under `endTimestamp`. Reading only the
  // end key attached the right dates to the wrong lead: "the new end date is outside the range this
  // survey allows", on an error about a release date. Found by review.
  const firstOf = (value: string | string[] | undefined): string | undefined =>
    Array.isArray(value) ? value[0] : value
  const message = [
    firstOf(record.endTimestamp),
    firstOf(record.startTimestamp),
    firstOf(record.earlyAccessReleaseTimestamp_1),
    firstOf(record.earlyAccessReleaseTimestamp_2),
    record.message
  ].find((candidate) => typeof candidate === 'string' && candidate.trim() !== '')

  const dates = BOUNDARY_DATE_LABELS.reduce<string[]>((acc, [key, label]) => {
    const formatted = formatDateByUserTimeZone(record[key])
    return formatted === '' ? acc : [...acc, `${label} ${formatted}`]
  }, [])

  if (message == null && dates.length === 0) return undefined
  // Which date the fallback names is decided by the keys that arrived, not assumed. A scheduled
  // reopen refused on its start used to be reported as an end-date problem.
  const hasStartBoundary = record.earliestAllowedStartTimestamp != null || record.latestAllowedStartTimestamp != null
  const hasEndBoundary = record.earliestAllowedEndTimestamp != null || record.latestAllowedEndTimestamp != null
  const refusedDate = hasStartBoundary && !hasEndBoundary
    ? 'the new start date'
    : hasEndBoundary && !hasStartBoundary ? 'the new end date' : 'those dates'
  const lead = message ?? `${refusedDate} ${refusedDate === 'those dates' ? 'are' : 'is'} outside the range this survey allows`
  // "your time" because the backend's own sentence now names the same instant in UTC. Without the
  // label the two readings look like two different dates rather than one shown twice — and the
  // local one is worth keeping, since it is the clock the admin is actually working from.
  //
  // Entries are joined by a space, so the sentence has to close itself or two refused sections
  // read as one run-on line. The period moves to the end rather than being dropped.
  return dates.length > 0 ? `${lead.replace(/\.$/, '')} (${dates.join('; ')}, your time).` : lead
}

/**
 * `detail` is the backend's own account of this section — a sentence, or a dict for `boundary`,
 * both handled by `readChangeDetail`. The reason map only covers its absence.
 *
 * A `partial` with no detail gets **no clause at all** — just the name. `partialLead` has already
 * said what half-happened for this operation, and the fallback phrase is written for a refusal:
 * appending "it could not be changed" to a section that *did* change would contradict the sentence
 * directly above it and tell the admin to redo work that already happened. A `failed` section with
 * no reason still gets the fallback, because there the phrase is true.
 */
const describeChange = (change: ITaskSectionChange, kind: SlotTaskKind): string => {
  const name = change.abbreviation ?? change.sectionId
  const detail = readChangeDetail(change.detail)
  if (detail != null) return `${name} — ${detail}`
  if (isPartial(change)) return name
  const reason = change.reason === 'wrong_status'
    ? BULK_STATUS_COPY[kind].wrongStatus
    : REASON_COPY[change.reason ?? ''] ?? 'it could not be changed'
  return `${name} — ${reason}`
}

const nameSections = (changes: ITaskSectionChange[], kind: SlotTaskKind): string => {
  const named = changes.slice(0, MAX_NAMED_SECTIONS).map((change) => describeChange(change, kind))
  const remainder = changes.length - named.length
  if (remainder > 0) named.push(`…and ${remainder.toLocaleString()} more.`)
  return named.join(' ')
}

/**
 * Toast copy for a finished bulk reopen / bulk go live.
 *
 * Partial success is the normal outcome, not an error — one shared end date is validated against
 * each section's own boundaries — so a run with refusals in it is reported as a warning naming the
 * sections that need attention, and only a run that changed nothing is an error.
 *
 * The two failure groups are kept apart because the admin's next step differs. A `failed` section
 * did not change and can be run again; a section reported `success` with `reason: "partial"` **is
 * live** and must not be — go-live commits the promotion before generating responses, so it has
 * already left `view=published` and a re-run would select nothing and appear to do nothing.
 */
/**
 * " to open on <date>", for a scheduled reopen that reported the start it resolved to.
 *
 * Read from the result rather than echoed from the form: the backend clamps a start that has passed
 * by the time the queued job reaches the section, so the submitted value and the real one can
 * differ. This sentence is where an admin finds that out.
 */
const scheduledOpening = (result: IBulkSectionStatusResult, kind: SlotTaskKind): string => {
  if (kind !== 'bulkReopenScheduled') return ''
  const opensOn = formatDateByUserTimeZone(result.startTimestamp)
  return opensOn === '' ? '' : ` to open on ${opensOn}`
}

const formatBulkSectionStatusToast = (
  result: IBulkSectionStatusResult,
  kind: SlotTaskKind
): SlotTaskToastContent => {
  const copy = BULK_STATUS_COPY[kind]
  const changes = result.changes ?? []
  const partials = changes.filter(isPartial)
  const failures = changes.filter((change) => change.status === 'failed')
  const succeeded = changes.filter((change) => change.status === 'success').length
  const attempted = result.total ?? changes.length
  const surveys = `course section survey${succeeded === 1 ? '' : 's'}`
  const live = kind === 'bulkGoLive' ? ' live' : ''
  const opensOn = scheduledOpening(result, kind)

  if (failures.length === 0 && partials.length === 0) {
    return {
      variant: 'success',
      title: 'Done',
      subTitle: `${copy.changed} ${succeeded.toLocaleString()} ${surveys}${live}${opensOn}.`
    }
  }

  const lead = succeeded > 0
    ? `${copy.changed} ${succeeded.toLocaleString()} of ${attempted.toLocaleString()} course section surveys${live}${opensOn}.`
    : copy.none
  const parts = [lead]
  if (partials.length > 0) {
    parts.push(`${copy.partialLead} ${nameSections(partials, kind)}`)
  }
  if (failures.length > 0) {
    parts.push(`Not changed: ${nameSections(failures, kind)}`)
  }
  if (partials.length + failures.length > MAX_NAMED_SECTIONS) {
    parts.push(ACTIVITY_MONITOR_HINT)
  }

  return {
    variant: succeeded > 0 ? 'warning' : 'error',
    title: succeeded > 0 ? copy.warningTitle : copy.errorTitle,
    subTitle: parts.join('\n')
  }
}

/**
 * Build toast title/subtitle for a terminal slot background-task poll result.
 *
 * After CE-218 ABC-4, partial-failure `data` may be a downloadable CSV
 * (`Section ID,status,details`). That CSV is for Activity Monitor download — never
 * dump it into the toast. Prefer `taskMetadata` counts / joined error_details when
 * present; otherwise parse CSV rows into short "Section: reason" lines and truncate.
 */
export const formatSlotTaskToast = (input: SlotTaskToastInput): SlotTaskToastContent => {
  const { status, data, taskMetadata, fallbackMessage, kind } = input

  // Before the sync branches: a bulk status result carries `updated` / `errors` counts too, so it
  // would otherwise be read as a sync summary and reported in sync copy. Its own `changes` array
  // is what identifies it; a run that refuses the whole project (root not LIVE) raises instead and
  // carries no changes, so it falls through to the generic error handling below.
  if (kind != null && isBulkSectionStatusResult(data)) {
    return formatBulkSectionStatusToast(data, kind)
  }

  const syncSource = resolveSyncToastSource(data, taskMetadata)
  const taskErrorMessage = getTaskErrorMessage(data)

  if (status === 'SUCCESSFUL') {
    return {
      variant: 'success',
      title: 'Done',
      subTitle: fallbackMessage
    }
  }

  if (status === 'WARNING') {
    if (syncSource.hasSyncSignal) {
      return {
        variant: 'warning',
        title: 'Sync completed with warnings',
        subTitle: buildSyncDetailsSubTitle(
          buildWarningLead(syncSource),
          syncSource.detailLines,
          { includeActivityMonitorWhenTruncated: true }
        )
      }
    }
    return {
      variant: 'warning',
      title: 'Completed with warnings',
      subTitle: taskErrorMessage || fallbackMessage || 'The action finished with warnings.'
    }
  }

  // ERROR — no Activity Monitor mention on non-truncated hard failures;
  // truncated sync-detail lists still point operators to the Activity Monitor download.
  if (syncSource.hasSyncSignal) {
    return {
      variant: 'error',
      title: 'Sync failed',
      subTitle: buildSyncDetailsSubTitle(
        'No course sections were synced.',
        syncSource.detailLines,
        { includeActivityMonitorWhenTruncated: false }
      )
    }
  }

  if (taskErrorMessage != null) {
    return {
      variant: 'error',
      title: 'Something went wrong',
      subTitle: taskErrorMessage
    }
  }

  return {
    variant: 'error',
    title: 'Something went wrong',
    subTitle: 'The action could not be completed. Please try again.'
  }
}
